import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { adminAPI } from "@/services/api";
import { toast } from "sonner";
import { BookOpen, CheckCircle, Clock, RefreshCw, ShieldCheck, ShoppingCart, Users, Zap } from "lucide-react";
import { ADMIN_RANKS, hasAdminRank, hasExplicitAdminRank, hasFullAdminRank } from "@/utils/adminRanks";
import { CardGrid, LoadingState, PageHeader, PageShell, QuickAction, SectionCard, StatCard } from "@/components/app/page";

export default function AdminDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcReport, setRecalcReport] = useState(null);

  const loadStats = useCallback(async () => {
    if (!hasFullAdminRank(user)) {
      setLoading(false);
      return;
    }

    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error loading stats", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) return <LoadingState />;

  const statCards = [
    {
      title: "Utilisateurs totaux",
      value: stats?.total_users || 0,
      icon: Users,
      tone: "blue",
      href: "/admin/users/all",
      description: "Ouvrir la liste des utilisateurs",
      ranks: [ADMIN_RANKS.FULL],
    },
    {
      title: "Partenaires",
      value: stats?.total_partners || 0,
      icon: Users,
      tone: "green",
      href: "/admin/users",
      description: "Acceder a la base des partenaires",
      ranks: [ADMIN_RANKS.FULL],
    },
    {
      title: "Produits",
      value: stats?.total_products || 0,
      icon: BookOpen,
      tone: "pine",
      href: "/admin/products",
      description: "Ouvrir la gestion des produits",
      ranks: [ADMIN_RANKS.FULL],
    },
    {
      title: "Produits en attente",
      value: stats?.pending_products || 0,
      icon: Clock,
      tone: "gold",
      href: "/admin/products",
      description: "Voir les produits a valider",
      ranks: [ADMIN_RANKS.FULL],
    },
    {
      title: "Commandes",
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      tone: "gold",
      href: "/orders",
      description: "Consulter les commandes",
      ranks: [ADMIN_RANKS.FULL],
    },
  ].filter((stat) => hasAdminRank(user, ...stat.ranks));

  const quickActions = [
    { href: "/admin/kyc", title: "Verification KYC", description: "Dossiers partenaires", icon: ShieldCheck, ranks: [ADMIN_RANKS.A], testId: "quick-action-kyc" },
    { href: "/admin/activations", title: "Gestion activations", description: "Paiements et lots d'activation", icon: Zap, ranks: [ADMIN_RANKS.B], testId: "quick-action-activations" },
    { href: "/admin/products", title: "valider les produits", description: hasFullAdminRank(user) ? `${stats?.pending_products || 0} en attente` : "Produits et commandes", icon: BookOpen, ranks: [ADMIN_RANKS.C], testId: "quick-action-validate-products" },
    { href: "/admin/catalogue-libre/submit", title: "Catalogue libre", description: "Déposer une oeuvre gratuite avec preuve des droits", icon: BookOpen, ranks: [ADMIN_RANKS.CL], explicitRank: ADMIN_RANKS.CL, testId: "quick-action-free-catalogue" },
    { href: "/admin/users/all", title: "Gestion des Utilisateurs", description: "Liste, roles et historique ISO", icon: Users, ranks: [ADMIN_RANKS.FULL], testId: "quick-action-users" },
    { href: "/admin/users", title: "Base partenaires", description: "Profils et lignees partenaires", icon: Users, ranks: [ADMIN_RANKS.FULL], testId: "quick-action-partners" },
    { href: "/orders", title: "Commandes", description: "Consulter les commandes PAE", icon: ShoppingCart, ranks: [ADMIN_RANKS.C], testId: "quick-action-orders" },
    { href: "/analytics", title: "Vue financiere", description: "Analytics et exports", icon: ShoppingCart, ranks: [ADMIN_RANKS.FULL], testId: "quick-action-finance" },
  ].filter((action) =>
    action.explicitRank ? hasExplicitAdminRank(user, action.explicitRank) : action.ranks.includes(ADMIN_RANKS.FULL) ? hasFullAdminRank(user) : hasAdminRank(user, ...action.ranks)
  );

  const handleRecalculate = async () => {
    setRecalculating(true);
    setRecalcReport(null);
    try {
      const res = await adminAPI.recalculateAll();
      setRecalcReport(res.data);
      toast.success(`Recalcul termine ! ${res.data.grand_total_mc} MC distribues`);
      loadStats();
    } catch (e) {
      toast.error("Erreur lors du recalcul");
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <PageShell data-testid="admin-dashboard">
      <PageHeader title="Tableau de bord administrateur" description="Vue de pilotage des validations, utilisateurs et controles internes." />

      {statCards.length > 0 && (
        <CardGrid>
          {statCards.map((stat, index) => (
            <Link
              key={stat.title}
              to={stat.href}
              className="block h-full rounded-xl outline-none transition-transform hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-[#2A4B46]/25"
              aria-label={`${stat.title} - ${stat.description}`}
              title={stat.description}
            >
              <StatCard
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                tone={stat.tone}
                testId={`stat-${index}`}
              />
            </Link>
          ))}
        </CardGrid>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <SectionCard title="Actions rapides">
          <div className="space-y-3">
            {quickActions.map((action) => (
              <QuickAction key={action.href} href={action.href} icon={action.icon} title={action.title} description={action.description} testId={action.testId} />
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Informations">
          <p className="leading-7 text-[#5C625F]">
            Bienvenue dans le panneau d'administration de MMJ. Vous pouvez valider les produits soumis,
            gerer les utilisateurs et suivre l'activité de la plateforme.
          </p>
        </SectionCard>
      </div>

      {hasFullAdminRank(user) && (
        <SectionCard title="Recalcul des bonus" className="border-l-4 border-l-[#D4A373]">
          <p className="mb-4 text-sm leading-6 text-[#5C625F]">
            Recalcule tous les niveaux des partenaires et tous les bonus-edition pour chaque commande.
            Les bonus de parrainage et d'équipe sont preserves.
          </p>
          <Button
            className="bg-[#D4A373] text-white hover:bg-[#c49362]"
            onClick={handleRecalculate}
            disabled={recalculating}
            data-testid="recalculate-button"
          >
            <RefreshCw size={16} className={recalculating ? "animate-spin" : ""} />
            {recalculating ? "Recalcul en cours..." : "Recalculer tous les bonus"}
          </Button>

          {recalcReport && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <ReportMetric label="Total distribue" value={`${recalcReport.grand_total_mc} MC`} />
                <ReportMetric label="Commandes traitees" value={recalcReport.orders_processed} />
                <ReportMetric label="Niveaux mis a jour" value={recalcReport.levels_updated} />
                <ReportMetric label="Erreurs" value={recalcReport.errors_count} danger />
              </div>

              <div className="rounded-xl border border-[#E6E4E0] bg-white p-4">
                <h4 className="mb-3 font-semibold text-[#2A4B46]">Repartition par categorie</h4>
                <div className="space-y-2">
                  {Object.entries(recalcReport.category_totals_mc || {}).map(([cat, mc]) => (
                    <div key={cat} className="flex items-center justify-between gap-3">
                      <span className="text-sm capitalize text-[#5C625F]">{cat}</span>
                      <span className="font-semibold text-[#2A4B46]">{mc} MC</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-[#E6E4E0] bg-white p-4">
                <h4 className="mb-3 font-semibold text-[#2A4B46]">Distribution des niveaux</h4>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(recalcReport.level_distribution || {}).map(([level, count]) => (
                    <div key={level} className="rounded-full bg-[#F3EFE9] px-3 py-1 text-sm">
                      <span className="font-semibold text-[#2A4B46]">{level}</span>: {count}
                    </div>
                  ))}
                </div>
              </div>

              {recalcReport.errors_count > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                  <p className="mb-2 text-sm font-semibold text-red-800">Erreurs ({recalcReport.errors_count})</p>
                  {recalcReport.errors?.slice(0, 5).map((error, index) => (
                    <p key={index} className="text-xs text-red-700">{error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </SectionCard>
      )}
    </PageShell>
  );
}

function ReportMetric({ label, value, danger = false }) {
  return (
    <div className="rounded-xl bg-[#F3EFE9] p-3">
      <p className="text-xs text-[#5C625F]">{label}</p>
      <p className={`text-xl font-bold ${danger ? "text-red-600" : "text-[#2A4B46]"}`}>{value}</p>
    </div>
  );
}
